import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { finalize } from "rxjs/operators";
import { Perfil } from "../../shared/model/security/perfil.model";
import { PerfilService } from "../perfil/perfil.service";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";

@Component({
  selector: "app-perfil",
  standalone: true,
  templateUrl: "./perfil.component.html",
  imports: [FormsModule, CommonModule, MatButtonModule, MatIconModule],
})
export class PerfilComponent implements OnInit {
  listaPerfil: Array<Perfil> = [];
  consultaObject = new ConsultaList<Perfil>();
  totalRegistros = 0;
  totalPages = 0;
  currentPage = 1;
  itemsPerPage = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  constructor(
    private perfilService: PerfilService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.applyCachedData();
    this.consultarPerfiles();
  }

  modificaperfil(objectPerfil: Perfil) {
    if (!objectPerfil?.id) {
      return;
    }
    this.router.navigate(["/admin/modificaperfil", objectPerfil.id]);
  }

  altaperfil() {
    this.router.navigate(["/admin/perfilalta"]);
  }

  consultarPerfiles(): void {
    this.loading = this.listaPerfil.length === 0;
    this.cdr.detectChanges();
    this.consultaObject.bean = new Perfil();
    this.consultaObject.regInicial = (this.currentPage - 1) * this.itemsPerPage;
    this.consultaObject.regXPagina = this.itemsPerPage;

    this.perfilService
      .consultarPerfil(this.consultaObject)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: ConsultaList<Perfil>) => {
          this.listaPerfil = [...(res?.list ?? [])];
          this.totalRegistros = Number(
            res?.pTotalItems ?? this.listaPerfil.length ?? 0
          );
          this.totalPages = Math.max(
            1,
            Math.ceil(this.totalRegistros / this.itemsPerPage)
          );
          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
          }
          this.perfilService.setPerfilesCache({
            response: res,
            currentPage: this.currentPage,
            itemsPerPage: this.itemsPerPage,
          });
          this.cdr.detectChanges();
        },
        error: () => {
          this.listaPerfil = [];
          this.totalRegistros = 0;
          this.totalPages = 1;
        },
      });
  }

  private applyCachedData(): void {
    const cache = this.perfilService.getPerfilesCache();
    if (!cache) {
      return;
    }

    this.currentPage = cache.currentPage;
    this.itemsPerPage = cache.itemsPerPage;
    this.listaPerfil = [...(cache.response.list ?? [])];
    this.totalRegistros = Number(
      cache.response.pTotalItems ?? this.listaPerfil.length ?? 0
    );
    this.totalPages = Math.max(1, Math.ceil(this.totalRegistros / this.itemsPerPage));
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.consultarPerfiles();
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = Number(value);
    this.currentPage = 1;
    this.consultarPerfiles();
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  getRangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRegistros);
  }

  getStatusLabel(perfil: Perfil): string {
    return perfil?.sitio ? "Activo" : "Inactivo";
  }

  getStatusClass(perfil: Perfil): string {
    return perfil?.sitio ? "is-active" : "is-inactive";
  }

  getSiteDescription(perfil: Perfil): string {
    return perfil?.sitio || "Sin sitio asignado";
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }
}
